export const itemHasVariations = (item) =>
  Array.isArray(item?.variations) && item.variations.length >= 2;

export const getItemPriceRows = (item) =>
  itemHasVariations(item) ? item.variations : [];

export const formatItemCanvasPrice = (item, formatPrice) => {
  // Variations array takes priority.
  if (itemHasVariations(item)) {
    return getItemPriceRows(item)
      .map((row) => formatPrice(row.price))
      .join("  /  ");
  }
  // Dual-price string e.g. "11.95/14.95".
  const raw = String(item?.price ?? "").trim();
  if (raw.includes("/")) {
    return raw
      .split("/")
      .map((p) => formatPrice(p.trim()))
      .join("  /  ");
  }
  // Single price.
  return formatPrice(item?.price);
};

// ─── Variation editing helpers ───────────────────────────────────────────────
// `item.variations` ([{ label, price }], price stored as a "9.99"-style string,
// matching what the extraction flow already writes) is the authoritative data.
// `price` ("9.99/11.99") and `priceHeading` ("Roll/Hero") are derived from it
// for older consumers (canvas preview, CSV export) and kept in sync here.

const parseVariationPrice = (value) => {
  const raw = String(value ?? "").trim().replace(/^\$/, "");
  if (!raw || !/^\d*\.?\d+$/.test(raw)) return null;
  const num = Number(raw);
  return Number.isFinite(num) && num > 0 ? num : null;
};

// Validates editor rows: every variation needs a name AND a price.
// Returns { ok, variations, errors } where errors is a list of
// { index, field: "label" | "price", message }.
export const validateVariationRows = (rows) => {
  const errors = [];
  const seen = new Map();
  const variations = [];

  (Array.isArray(rows) ? rows : []).forEach((row, index) => {
    const label = String(row?.label ?? "").trim();
    const price = parseVariationPrice(row?.price);
    if (!label) {
      errors.push({ index, field: "label", message: "Enter a name for this variation." });
    } else {
      const dupKey = label.toLowerCase();
      if (seen.has(dupKey)) {
        errors.push({
          index,
          field: "label",
          message: `"${label}" is used more than once. Variation names must be unique.`,
        });
      }
      seen.set(dupKey, index);
    }
    if (price == null) {
      errors.push({ index, field: "price", message: "Enter a price greater than 0." });
    }
    variations.push({ label, price: price == null ? "" : price.toFixed(2) });
  });

  return { ok: errors.length === 0, variations, errors };
};

export const variationsToPriceString = (variations) =>
  (variations || []).map((v) => v.price).join("/");

// "Roll/Hero" - only when every option has a name (price-only options have none).
export const variationsToHeading = (variations) =>
  (variations || []).length && variations.every((v) => !v.unnamed)
    ? variations.map((v) => v.label).join("/")
    : "";

// Builds the saved item from the edit form WITHOUT dropping anything the form
// does not own. `existing` is the item being edited (null when creating).
//
// values.variations:
//   undefined -> leave variations, price and priceHeading as they are
//   2+ rows   -> store them, derive price + priceHeading
//   1 row     -> collapse to a plain single price (no modal, no stale heading)
//   0 rows    -> single-price item; an explicit `variations: []` is kept on an
//                item that had variations so the server can tell "removed"
//                apart from "field omitted"
// Throws if the rows are invalid; callers validate first to show errors.
export const buildItemFromForm = (existing, values, makeId = () => Date.now().toString()) => {
  const base = existing ? { ...existing } : { id: makeId(), isVisible: true };
  const next = { ...base, name: String(values.name ?? base.name ?? "").trim() };
  if (existing) next.isVisible = existing.isVisible !== false;

  const setOrClear = (key, value) => {
    const text = value == null ? "" : String(value).trim();
    if (text) next[key] = text;
    else delete next[key];
  };

  setOrClear("description", values.description);

  if (!Array.isArray(values.variations)) {
    // Caller did not touch variations: only the plain price fields may change.
    if (!itemHasVariations(existing)) {
      setOrClear("price", values.price);
      setOrClear("priceHeading", values.priceHeading);
    }
    return next;
  }

  const hadVariations = Array.isArray(existing?.variations) && existing.variations.length > 0;

  if (values.variations.length === 0) {
    if (hadVariations) next.variations = [];
    else delete next.variations;
    setOrClear("price", values.price);
    setOrClear("priceHeading", values.priceHeading);
    return next;
  }

  const { ok, variations } = validateVariationRows(values.variations);
  if (!ok) throw new Error("Invalid variations");

  if (variations.length === 1) {
    if (hadVariations) next.variations = [];
    else delete next.variations;
    next.price = variations[0].price;
    setOrClear("priceHeading", values.priceHeading);
    return next;
  }

  next.variations = variations;
  next.price = variationsToPriceString(variations);
  setOrClear("priceHeading", variationsToHeading(variations));
  return next;
};

// Items saved by the old editor lost their `variations` array and kept only
// the combined strings ("9.99/11.99" + "Roll/Hero"). This turns those back
// into editor ROWS so the admin can confirm and save them as real variations.
// It is an editing aid only: the cart and ordering page never read the strings.
export const rowsFromLegacyPrice = (price, priceHeading) => {
  const prices = String(price ?? "").split("/").map((p) => p.trim());
  if (prices.length < 2 || prices.some((p) => parseVariationPrice(p) == null)) return [];
  const labels = String(priceHeading ?? "").split("/").map((l) => l.trim());
  return prices.map((p, i) => ({ label: labels.length === prices.length ? labels[i] : "", price: p }));
};
